import getApi, { getApiBaseUrl } from '../api';
import {
  PaginatedStocktakingResponse,
  StocktakingDocument,
  StocktakingSession,
  StocktakingSessionItem,
  StockPointsResponse,
} from '../types/stocktaking';

export interface ListStocktakingParams {
  page?: number;
  limit?: number;
  state?: string;
  search?: string;
}

const api = () => getApi();

export async function fetchStocktakings(params: ListStocktakingParams = {}): Promise<PaginatedStocktakingResponse> {
  const response = await api().get('/fortnox/stocktakings/', { params });
  const data = response.data;
  if (Array.isArray(data)) {
    return { results: data };
  }
  if (data?.StockTakings && Array.isArray(data.StockTakings)) {
    return { results: data.StockTakings, count: data.StockTakings.length };
  }
  if (data?.stockTakings && Array.isArray(data.stockTakings)) {
    return { results: data.stockTakings, count: data.stockTakings.length };
  }
  return data as PaginatedStocktakingResponse;
}

export async function fetchStocktakingDetail(id: string | number): Promise<StocktakingDocument> {
  const response = await api().get(`/fortnox/stocktakings/${id}/`);
  const doc = response.data?.StockTaking ?? response.data;
  if (doc?.rows && Array.isArray(doc.rows)) {
    doc.rows = doc.rows.map((row: any) => ({
      ...row,
      stockTakenQuantity: Number(row.stockTakenQuantity ?? row.countedQuantity ?? 0),
    }));
  }
  return doc;
}

export async function fetchStocktakingRows(id: string | number): Promise<StocktakingSessionItem[]> {
  const response = await api().get(`/fortnox/stocktakings/${id}/rows/`);
  const rows = response.data?.rows ?? response.data?.Rows ?? response.data;
  if (Array.isArray(rows)) {
    return rows.map(row => ({
      ...row,
      countedQuantity: Number(row.stockTakenQuantity ?? row.countedQuantity ?? 0),
      stockTakenQuantity: Number(row.stockTakenQuantity ?? row.countedQuantity ?? 0),
      totalQuantityInStock: row.totalQuantityInStock ?? null,
      stockLocationCode: row.stockLocationCode ?? null,
    }));
  }
  return [];
}

export async function fetchStockPoints(): Promise<StockPointsResponse> {
  const response = await api().get('/fortnox/stockpoints/');
  return response.data;
}

export type UpdateStocktakingPayload = StocktakingDocument | { StockTaking: StocktakingDocument };

export async function updateStocktaking(id: string | number, payload: UpdateStocktakingPayload): Promise<StocktakingDocument> {
  const body = 'StockTaking' in payload ? payload : { StockTaking: payload };
  const response = await api().put(`/fortnox/stocktakings/${id}/`, body);
  const doc = response.data?.StockTaking ?? response.data;
  return doc;
}

// Session helpers
export async function startStocktakingSession(body: Partial<StocktakingSession> & { name: string; fortnoxId?: string | number }): Promise<StocktakingSession> {
  const response = await api().post('/api/stocktaking-sessions/', body);
  return response.data;
}


export async function listSessionItems(sessionId: number): Promise<StocktakingSessionItem[]> {
  const response = await api().get(`/api/stocktaking-sessions/${sessionId}/items/`);
  const data = response.data?.items ?? response.data;
  if (Array.isArray(data)) {
    return data.map(item => ({
      ...item,
      countedQuantity: Number(item.countedQuantity ?? item.stockTakenQuantity ?? 0),
      stockTakenQuantity: Number(item.stockTakenQuantity ?? item.countedQuantity ?? 0),
    }));
  }
  return [];
}

export async function bulkAddSessionItems(sessionId: number, items: Array<{ itemId: string }>): Promise<StocktakingSessionItem[]> {
  const response = await api().post(`/api/stocktaking-sessions/${sessionId}/items/`, { items });
  const data = response.data?.items ?? response.data;
  return Array.isArray(data) ? data : [];
}

export async function updateSessionItem(sessionId: number, itemId: string, payload: Partial<StocktakingSessionItem>): Promise<StocktakingSessionItem> {
  const response = await api().post(`/api/stocktaking-sessions/${sessionId}/items/${encodeURIComponent(itemId)}/`, payload);
  return response.data;
}

export interface FinalizeSessionPayload {
  fortnoxId: string | number;
  comments?: string;
}

export async function finalizeSession(sessionId: number, payload: FinalizeSessionPayload): Promise<{ status: string }> {
  // Send as-is with camelCase keys
  const backendPayload = {
    fortnoxId: String(payload.fortnoxId),
    comments: payload.comments || 'Inventering klar'
  };
  const response = await api().post(`/api/stocktaking-sessions/${sessionId}/finalize/`, backendPayload);
  return response.data;
}

export function buildStocktakingWsUrl(sessionId: number): string {
  const rawBase = getApiBaseUrl();
  // Ensure we only keep origin (scheme + host + port)
  let origin: string;
  try {
    const parsed = new URL(rawBase.includes('://') ? rawBase : `http://${rawBase}`);
    origin = `${parsed.protocol}//${parsed.host}`; // strip path if user accidentally added
  } catch {
    // Fallback assume http
    origin = rawBase.startsWith('http') ? rawBase : `http://${rawBase}`;
    origin = origin.replace(/\/$/, '');
  }

  const isSecure = origin.startsWith('https://');
  const wsScheme = isSecure ? 'wss://' : 'ws://';
  // If backend expects api-prefixed WS path in dev adjust here if needed; currently using /ws/stocktaking/
  const path = `/ws/stocktaking/${sessionId}/`;
  const full = `${wsScheme}${origin.replace(/^https?:\/\//, '')}${path}`;
  const url = new URL(full);
  const jwtToken = window.localStorage.getItem('token');
  if (jwtToken) url.searchParams.set('jwt', jwtToken);
  // Pass environment hint for server-side diagnostics
  const env = window.localStorage.getItem('apiEnvironment');
  if (env) url.searchParams.set('env', env);
  return url.toString();
}
